import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import WizardShell, { ClinicalInsight } from "../components/WizardShell";
import BIAThresholdCalibration from "../components/BIAThresholdCalibration";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Settings,
  Grid3X3,
  Users,
  Building2,
  Truck,
  Clock,
  Save,
  CheckCircle2,
  AlertTriangle,
  Info,
  ChevronRight,
  ChevronLeft,
  Target,
  Shield,
  Zap,
  Link2,
  Calculator,
  FileCheck,
  Plus,
  Trash2,
  Edit2,
  Eye,
  RefreshCw,
  DollarSign,
  Scale,
  History,
  Bell,
  Loader2,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Time band labels
const TIME_BANDS = [
  { id: "band_1", label: "0-4 Hours", hours: 4 },
  { id: "band_2", label: "4-24 Hours", hours: 24 },
  { id: "band_3", label: "24-72 Hours", hours: 72 },
  { id: "band_4", label: "72+ Hours", hours: 168 },
];

// Impact categories for definitions
const IMPACT_DEFINITION_CATEGORIES = [
  { key: "financial", label: "Financial Impact", icon: DollarSign, color: "text-emerald-600", bgColor: "bg-emerald-50" },
  { key: "legal", label: "Legal Impact", icon: Scale, color: "text-blue-600", bgColor: "bg-blue-50" },
  { key: "regulatory", label: "Regulatory Impact", icon: Shield, color: "text-purple-600", bgColor: "bg-purple-50" },
  { key: "reputational", label: "Reputational Impact", icon: Users, color: "text-amber-600", bgColor: "bg-amber-50" },
  { key: "operational", label: "Operational Impact", icon: Settings, color: "text-red-600", bgColor: "bg-red-50" },
];

// Impact levels for definitions
const IMPACT_DEFINITION_LEVELS = [
  { key: "high", label: "High", color: "bg-red-100 text-red-700 border-red-300" },
  { key: "medium", label: "Medium", color: "bg-amber-100 text-amber-700 border-amber-300" },
  { key: "low", label: "Low", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  { key: "none", label: "No Impact", color: "bg-slate-100 text-slate-600 border-slate-300" },
];

// Impact categories (for legacy matrix view)
const IMPACT_CATEGORIES = [
  { id: "financial", label: "Financial", description: "Revenue loss, costs, penalties" },
  { id: "operational", label: "Operational", description: "Service delivery, productivity" },
  { id: "reputational", label: "Reputational", description: "Brand damage, customer trust" },
  { id: "legal", label: "Legal/Contractual", description: "Lawsuits, contract breaches" },
  { id: "regulatory", label: "Regulatory", description: "Compliance violations, fines" },
];

// Impact levels (for legacy matrix)
const IMPACT_LEVELS = [
  { value: "no_impact", label: "No Impact", color: "bg-slate-100 text-slate-600" },
  { value: "low", label: "Low", color: "bg-green-100 text-green-700" },
  { value: "medium", label: "Medium", color: "bg-amber-100 text-amber-700" },
  { value: "high", label: "High", color: "bg-red-100 text-red-700" },
];

// Configuration steps with ISO 22301 clause mapping
const CONFIG_STEPS = [
  { id: 1, title: "Impact Definitions", description: "Define High/Medium/Low/None for each impact category (ISO 22301:8.2.2)", icon: Grid3X3, clause: "8.2.2" },
  { id: 2, title: "Authority Matrix", description: "Map executives to BCM roles", icon: Users, clause: "5.3" },
  { id: 3, title: "Vendor Criticality", description: "View inherited vendor requirements (from BIA)", icon: Truck, clause: "8.2.2" },
  { id: 4, title: "Facility Criticality", description: "View inherited facility requirements (from BIA)", icon: Building2, clause: "8.2.2" },
  { id: 5, title: "Review & Activate", description: "Review and activate configuration", icon: CheckCircle2, clause: "8.2.1" },
];

// BCM Authority Roles (to be mapped from executives)
const BCM_AUTHORITY_ROLES = [
  { role_type: "crisis_director", role_name: "Crisis Director", description: "Declares emergency status and overall crisis leadership" },
  { role_type: "bcm_coordinator", role_name: "BCM Coordinator", description: "Coordinates recovery activities across departments" },
  { role_type: "it_recovery_lead", role_name: "IT Recovery Lead", description: "Manages IT systems and infrastructure recovery" },
  { role_type: "communications_lead", role_name: "Communications Lead", description: "Handles internal/external communications during crisis" },
  { role_type: "operations_lead", role_name: "Operations Lead", description: "Manages business operations continuity" },
  { role_type: "hr_lead", role_name: "HR/People Lead", description: "Manages staff welfare and HR-related crisis response" },
  { role_type: "finance_lead", role_name: "Finance Lead", description: "Manages financial aspects of crisis response" },
];

export default function BIAConfigurationPage() {
  const { user, api, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  
  // Configuration state
  const [impactMatrix, setImpactMatrix] = useState({});
  const [authorityMatrix, setAuthorityMatrix] = useState({ roles: [] });
  const [vendorTemplates, setVendorTemplates] = useState([]);
  const [facilityTemplates, setFacilityTemplates] = useState([]);
  const [configStatus, setConfigStatus] = useState({
    impact_definitions_configured: false,
    authority_matrix_defined: false,
    vendor_templates_count: 0,
    facility_templates_count: 0,
  });

  // NEW: Executives from Organization Management (for Authority Matrix dropdown)
  const [executives, setExecutives] = useState([]);
  const [executivesLoading, setExecutivesLoading] = useState(false);
  
  // NEW: Facility Criticality Summary (inherited from BIA)
  const [facilityCriticality, setFacilityCriticality] = useState({ facilities: [], summary: {} });
  const [facilityCriticalityLoading, setFacilityCriticalityLoading] = useState(false);
  
  // NEW: Vendor Criticality Summary (inherited from BIA)
  const [vendorCriticality, setVendorCriticality] = useState({ vendors: [], summary: {} });
  const [vendorCriticalityLoading, setVendorCriticalityLoading] = useState(false);

  // Impact Definitions state
  const [impactDefinitions, setImpactDefinitions] = useState(null);
  const [impactDefinitionsVersion, setImpactDefinitionsVersion] = useState(0);
  const [impactDefinitionsHistory, setImpactDefinitionsHistory] = useState([]);
  const [selectedDefinitionCategory, setSelectedDefinitionCategory] = useState("financial");
  const [updateStatus, setUpdateStatus] = useState(null);
  
  // NEW: Impact Definition Wizard Stage (1=Financial, 2=Legal, 3=Regulatory, 4=Reputational, 5=Operational, 6=Preview)
  const [impactWizardStage, setImpactWizardStage] = useState(1);
  const IMPACT_WIZARD_STAGES = [
    { id: 1, key: "financial", label: "Financial Impact", icon: DollarSign, color: "text-emerald-600", bgColor: "bg-emerald-50" },
    { id: 2, key: "legal", label: "Legal Impact", icon: Scale, color: "text-blue-600", bgColor: "bg-blue-50" },
    { id: 3, key: "regulatory", label: "Regulatory Impact", icon: Shield, color: "text-purple-600", bgColor: "bg-purple-50" },
    { id: 4, key: "reputational", label: "Reputational Impact", icon: Users, color: "text-amber-600", bgColor: "bg-amber-50" },
    { id: 5, key: "operational", label: "Operational Impact", icon: Settings, color: "text-red-600", bgColor: "bg-red-50" },
    { id: 6, key: "preview", label: "Preview & Save", icon: Eye, color: "text-sky-700", bgColor: "bg-sky-50" },
  ];

  // New item forms
  const [newVendor, setNewVendor] = useState({ vendor_name: "", vendor_type: "technology", criticality: "medium" });
  const [newFacility, setNewFacility] = useState({ facility_name: "", facility_type: "primary", activation_time_hours: 0 });
  const [newRole, setNewRole] = useState({ role_type: "", role_name: "", person_name: "", email: "", phone: "" });

  const canEdit = user?.role === "bcm_manager" || user?.role === "admin";

  // Context help for each wizard step
  const STEP_CONTEXT_HELP = {
    1: { title: "Impact Definitions", sections: [
      { heading: "About Impact Categories", content: "Define what High, Medium, Low, and None mean for each of the 5 impact categories. These definitions drive the 5x4 impact matrix used in every BIA assessment." },
      { heading: "Best Practice", content: "Start with Financial Impact - it's the most quantifiable. Use actual dollar thresholds from your organization. Regulatory and Reputational impacts should reference your specific industry requirements." },
    ], isoClause: "ISO 22301 - Clause 8.2.2" },
    2: { title: "Authority Matrix", sections: [
      { heading: "About Authority Mapping", content: "Map your organization's executives to predefined BCM authority roles. Each role has specific crisis response responsibilities." },
      { heading: "Best Practice", content: "Ensure the Crisis Director has authority to declare emergencies. Map at least 3 key roles before activating the configuration." },
    ], isoClause: "ISO 22301 - Clause 5.3" },
    3: { title: "Vendor Criticality", sections: [
      { heading: "About Vendor Dependencies", content: "This view shows vendor criticality inherited from completed BIA assessments. Vendors flagged as critical need dedicated recovery plans." },
      { heading: "Best Practice", content: "Review vendor SLAs against your recovery time objectives. Technology and data processing vendors typically carry the highest criticality." },
    ], isoClause: "ISO 22301 - Clause 8.2.2" },
    4: { title: "Facility Criticality", sections: [
      { heading: "About Facility Requirements", content: "Facility criticality is derived from BIA assessments. Facilities hosting critical processes require documented alternate site arrangements." },
      { heading: "Best Practice", content: "Ensure alternate facilities can be activated within your shortest RTO band. Primary data centers should always have warm standby sites." },
    ], isoClause: "ISO 22301 - Clause 8.2.2" },
    5: { title: "Review & Activate", sections: [
      { heading: "About Activation", content: "Review all configured items before activating the BIA framework. Once activated, Business Users can begin creating BIA assessments." },
      { heading: "Important", content: "Impact Definitions and Authority Matrix (Steps 1-2) must be configured before activation. Vendor and Facility data is inherited from BIA assessments." },
    ], isoClause: "ISO 22301 - Clause 8.2.1" },
  };

  const isStepComplete = (stepId) => {
    if (stepId === 1) return configStatus.impact_definitions_configured;
    if (stepId === 2) return configStatus.authority_matrix_defined;
    if (stepId === 3) return configStatus.vendor_templates_count > 0;
    if (stepId === 4) return configStatus.facility_templates_count > 0;
    if (stepId === 5) return getCompletionPercentage() >= 60;
    return false;
  };

  // Fetch Impact Definitions
  const fetchImpactDefinitions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/impact-definitions/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setImpactDefinitions(data.definitions);
        setImpactDefinitionsVersion(data.version || 0);
        setConfigStatus(prev => ({ ...prev, impact_definitions_configured: !data.is_default }));
      }
    } catch (error) {
      console.error("Error fetching impact definitions:", error);
    }
  }, [token]);

  // Fetch Impact Definitions History
  const fetchDefinitionsHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/impact-definitions/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setImpactDefinitionsHistory(data.history || []);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  }, [token]);

  // Fetch BIA Update Status
  const fetchUpdateStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/impact-definitions/bia-update-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUpdateStatus(data);
      }
    } catch (error) {
      console.error("Error fetching update status:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchConfiguration();
    fetchImpactDefinitions();
    fetchDefinitionsHistory();
    fetchUpdateStatus();
    fetchExecutives();
    fetchFacilityCriticality();
    fetchVendorCriticality();
  }, [fetchImpactDefinitions, fetchDefinitionsHistory, fetchUpdateStatus]);

  // Fetch executives from Organization Management
  const fetchExecutives = async () => {
    setExecutivesLoading(true);
    try {
      const response = await api.get("/bcm-admin/executives");
      setExecutives(response.data || []);
    } catch (error) {
      console.error("Failed to fetch executives:", error);
      setExecutives([]);
    } finally {
      setExecutivesLoading(false);
    }
  };

  // Fetch facility criticality summary (inherited from BIA)
  const fetchFacilityCriticality = async () => {
    setFacilityCriticalityLoading(true);
    try {
      const response = await api.get("/bia-engine/facilities/criticality-summary");
      setFacilityCriticality(response.data || { facilities: [], summary: {} });
    } catch (error) {
      console.error("Failed to fetch facility criticality:", error);
      setFacilityCriticality({ facilities: [], summary: {} });
    } finally {
      setFacilityCriticalityLoading(false);
    }
  };

  // Fetch vendor criticality summary (inherited from BIA)
  const fetchVendorCriticality = async () => {
    setVendorCriticalityLoading(true);
    try {
      const response = await api.get("/bia-engine/vendors/criticality-summary");
      setVendorCriticality(response.data || { vendors: [], summary: {} });
    } catch (error) {
      console.error("Failed to fetch vendor criticality:", error);
      setVendorCriticality({ vendors: [], summary: {} });
    } finally {
      setVendorCriticalityLoading(false);
    }
  };

  const fetchConfiguration = async () => {
    try {
      const response = await api.get("/bia-engine/governance");
      const data = response.data;
      
      // Load Impact Over Time Matrix
      if (data.impact_over_time_matrix) {
        setImpactMatrix(data.impact_over_time_matrix);
      } else {
        // Initialize with defaults
        initializeImpactMatrix();
      }
      
      // Load Authority Matrix
      if (data.authority_matrix) {
        setAuthorityMatrix(data.authority_matrix);
      }
      
      // Load Vendor Templates
      if (data.vendor_templates) {
        setVendorTemplates(data.vendor_templates);
      }
      
      // Load Facility Templates
      if (data.facility_templates) {
        setFacilityTemplates(data.facility_templates);
      }
      
      // Update config status
      setConfigStatus({
        impact_matrix_configured: !!data.impact_over_time_matrix,
        authority_matrix_defined: data.authority_matrix?.roles?.length > 0,
        vendor_templates_count: data.vendor_templates?.length || 0,
        facility_templates_count: data.facility_templates?.length || 0,
      });
      
    } catch (error) {
      console.error("Failed to fetch configuration:", error);
      initializeImpactMatrix();
    } finally {
      setLoading(false);
    }
  };

  const initializeImpactMatrix = () => {
    const defaultMatrix = {};
    IMPACT_CATEGORIES.forEach(cat => {
      defaultMatrix[cat.id] = {
        band_1: "no_impact",
        band_2: "no_impact",
        band_3: "low",
        band_4: "medium",
      };
    });
    setImpactMatrix(defaultMatrix);
  };

  // Step 1: Save Impact Matrix
  const saveImpactMatrix = async () => {
    if (!canEdit) {
      toast.error("BCM Manager access required");
      return;
    }
    
    setSaving(true);
    try {
      await api.post("/bia-engine/governance/impact-matrix", impactMatrix);
      toast.success("Impact Over Time Matrix saved!");
      setConfigStatus(prev => ({ ...prev, impact_matrix_configured: true }));
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to save Impact Matrix"));
    } finally {
      setSaving(false);
    }
  };

  // Impact Definitions: Update a specific field
  const updateImpactDefinition = (category, level, field, value) => {
    setImpactDefinitions(prev => ({
      ...prev,
      [category]: {
        ...prev?.[category],
        [level]: {
          ...prev?.[category]?.[level],
          [field]: value
        }
      }
    }));
  };

  // Impact Definitions: Update examples array
  const updateImpactDefinitionExamples = (category, level, examples) => {
    const examplesArray = examples.split("\n").filter(e => e.trim());
    updateImpactDefinition(category, level, "examples", examplesArray);
  };

  // Impact Definitions: Save
  const saveImpactDefinitions = async () => {
    if (!canEdit) {
      toast.error("BCM Manager access required");
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/impact-definitions/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ definitions: impactDefinitions }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setImpactDefinitionsVersion(data.version);
        setConfigStatus(prev => ({ ...prev, impact_definitions_configured: true }));
        toast.success(
          `Impact definitions saved! Version ${data.version} created. ${data.users_notified} users notified.`
        );
        fetchDefinitionsHistory();
        fetchUpdateStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save definitions");
      }
    } catch (error) {
      console.error("Error saving definitions:", error);
      toast.error("Failed to save impact definitions");
    } finally {
      setSaving(false);
    }
  };

  // Step 2: Save Authority Matrix
  const saveAuthorityMatrix = async () => {
    if (!canEdit) {
      toast.error("BCM Manager access required");
      return;
    }
    
    setSaving(true);
    try {
      await api.post("/bia-engine/governance/authority-matrix", authorityMatrix);
      toast.success("Authority Matrix saved!");
      setConfigStatus(prev => ({ ...prev, authority_matrix_defined: authorityMatrix.roles.length > 0 }));
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to save Authority Matrix"));
    } finally {
      setSaving(false);
    }
  };

  // Step 3: Save Vendor Templates
  const saveVendorTemplates = async () => {
    if (!canEdit) {
      toast.error("BCM Manager access required");
      return;
    }
    
    setSaving(true);
    try {
      await api.post("/bia-engine/governance/vendor-templates", { templates: vendorTemplates });
      toast.success("Vendor Templates saved!");
      setConfigStatus(prev => ({ ...prev, vendor_templates_count: vendorTemplates.length }));
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to save Vendor Templates"));
    } finally {
      setSaving(false);
    }
  };

  // Step 4: Save Facility Templates
  const saveFacilityTemplates = async () => {
    if (!canEdit) {
      toast.error("BCM Manager access required");
      return;
    }
    
    setSaving(true);
    try {
      await api.post("/bia-engine/governance/facility-templates", { templates: facilityTemplates });
      toast.success("Facility Templates saved!");
      setConfigStatus(prev => ({ ...prev, facility_templates_count: facilityTemplates.length }));
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to save Facility Templates"));
    } finally {
      setSaving(false);
    }
  };

  // Add vendor template
  const addVendorTemplate = () => {
    if (!newVendor.vendor_name) {
      toast.error("Vendor name is required");
      return;
    }
    setVendorTemplates([...vendorTemplates, { ...newVendor, id: Date.now().toString() }]);
    setNewVendor({ vendor_name: "", vendor_type: "technology", criticality: "medium" });
    toast.success("Vendor template added");
  };

  // Add facility template
  const addFacilityTemplate = () => {
    if (!newFacility.facility_name) {
      toast.error("Facility name is required");
      return;
    }
    setFacilityTemplates([...facilityTemplates, { ...newFacility, id: Date.now().toString() }]);
    setNewFacility({ facility_name: "", facility_type: "primary", activation_time_hours: 0 });
    toast.success("Facility template added");
  };

  // Add role to authority matrix
  const addRole = () => {
    if (!newRole.role_name) {
      toast.error("Role name is required");
      return;
    }
    setAuthorityMatrix(prev => ({
      ...prev,
      roles: [...prev.roles, { ...newRole, id: Date.now().toString(), status: "unassigned" }]
    }));
    setNewRole({ role_type: "", role_name: "", person_name: "", email: "", phone: "" });
    toast.success("Role added");
  };

  // Remove items
  const removeVendor = (id) => setVendorTemplates(vendorTemplates.filter(v => v.id !== id));
  const removeFacility = (id) => setFacilityTemplates(facilityTemplates.filter(f => f.id !== id));
  const removeRole = (id) => setAuthorityMatrix(prev => ({ ...prev, roles: prev.roles.filter(r => r.id !== id) }));

  // Impact level ordering for monotonic escalation enforcement
  const IMPACT_RANK = { no_impact: 0, low: 1, medium: 2, high: 3 };
  const BAND_ORDER = ["band_1", "band_2", "band_3", "band_4"];

  // Update impact matrix cell with monotonic escalation enforcement
  const updateImpactCell = (category, band, value) => {
    setImpactMatrix(prev => {
      const row = { ...prev[category] };
      const bandIdx = BAND_ORDER.indexOf(band);
      const newRank = IMPACT_RANK[value] ?? 0;

      row[band] = value;

      // Forward cascade: later bands must be >= this value
      for (let i = bandIdx + 1; i < BAND_ORDER.length; i++) {
        if ((IMPACT_RANK[row[BAND_ORDER[i]]] ?? 0) < newRank) {
          row[BAND_ORDER[i]] = value;
        }
      }
      // Backward cascade: earlier bands must be <= this value
      for (let i = bandIdx - 1; i >= 0; i--) {
        if ((IMPACT_RANK[row[BAND_ORDER[i]]] ?? 0) > newRank) {
          row[BAND_ORDER[i]] = value;
        }
      }

      return { ...prev, [category]: row };
    });
  };

  // Calculate configuration completeness
  const getCompletionPercentage = () => {
    let completed = 0;
    if (configStatus.impact_matrix_configured) completed++;
    if (configStatus.authority_matrix_defined) completed++;
    if (configStatus.vendor_templates_count > 0) completed++;
    if (configStatus.facility_templates_count > 0) completed++;
    return Math.round((completed / 4) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="bia-config-loading">
        <div className="w-8 h-8 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="bia-config-access-denied">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900">BCM Manager Access Required</h2>
            <p className="text-slate-600 mt-2">
              Only BCM Manageristrators can configure BIA settings. Contact your administrator for access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <WizardShell
      title="BIA Configuration"
      steps={CONFIG_STEPS.map(s => ({
        title: s.title,
        subtitle: s.description,
        isComplete: isStepComplete(s.id),
      }))}
      currentStep={currentStep - 1}
      onStepChange={(idx) => setCurrentStep(idx + 1)}
      completionPct={getCompletionPercentage()}
      contextHelp={STEP_CONTEXT_HELP[currentStep]}
      onBack={currentStep > 1 ? () => setCurrentStep(s => Math.max(1, s - 1)) : null}
      onContinue={currentStep < 5 ? () => setCurrentStep(s => Math.min(5, s + 1)) : null}
      continueLabel={currentStep === 5 ? "Finish" : "Next Step"}
    >
      <div data-testid="bia-configuration-page">
        {/* Step Header */}
        <div className="flex items-center gap-3 mb-6">
          {(() => {
            const step = CONFIG_STEPS[currentStep - 1];
            const Icon = step.icon;
            return <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center"><Icon className="w-5 h-5 text-sky-700" /></div>;
          })()}
          <div className="flex-1">
            <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Step {currentStep}: {CONFIG_STEPS[currentStep - 1].title}
            </h2>
            <p className="text-sm text-slate-500">{CONFIG_STEPS[currentStep - 1].description}</p>
          </div>
          {CONFIG_STEPS[currentStep - 1].clause && (
            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
              ISO 22301:{CONFIG_STEPS[currentStep - 1].clause}
            </Badge>
          )}
        </div>
          {/* Step 1: Impact Definitions - Guided Workflow */}
          {currentStep === 1 && (
            <div className="space-y-6" data-testid="step-impact-definitions">
              {/* Part 2B — Auto-calibrated impact thresholds from enrichment */}
              <BIAThresholdCalibration orgId={user?.org_id} />
              {/* Wizard Progress Bar */}
              <div className="bg-slate-50 rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-slate-700">Impact Definition Wizard</h4>
                  <Badge variant="outline" className="bg-white">
                    Stage {impactWizardStage} of 6
                  </Badge>
                </div>
                <div className="flex items-center gap-1">
                  {IMPACT_WIZARD_STAGES.map((stage, idx) => {
                    const Icon = stage.icon;
                    const isActive = impactWizardStage === stage.id;
                    const isComplete = impactWizardStage > stage.id;
                    return (
                      <div key={stage.id} className="flex items-center flex-1">
                        <button
                          onClick={() => setImpactWizardStage(stage.id)}
                          className={`flex flex-col items-center p-2 rounded-lg transition-all w-full ${
                            isActive 
                              ? `${stage.bgColor} ring-2 ring-offset-1 ring-indigo-400` 
                              : isComplete 
                                ? "bg-emerald-50 hover:bg-emerald-100" 
                                : "bg-white hover:bg-slate-50 border"
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isActive ? stage.bgColor : isComplete ? "bg-emerald-100" : "bg-slate-100"
                          }`}>
                            {isComplete ? (
                              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <Icon className={`w-4 h-4 ${isActive ? stage.color : "text-slate-400"}`} />
                            )}
                          </div>
                          <span className={`text-[10px] font-medium mt-1 ${isActive ? stage.color : isComplete ? "text-emerald-600" : "text-slate-500"}`}>
                            {stage.label.split(" ")[0]}
                          </span>
                        </button>
                        {idx < IMPACT_WIZARD_STAGES.length - 1 && (
                          <ChevronRight className={`w-4 h-4 mx-1 flex-shrink-0 ${isComplete ? "text-emerald-400" : "text-slate-300"}`} />
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* Progress percentage */}
                <div className="mt-3">
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-sky-700 to-teal-600 transition-all duration-300"
                      style={{ width: `${((impactWizardStage - 1) / 5) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1 text-center">
                    {impactWizardStage === 6 ? "Ready to save!" : `${Math.round(((impactWizardStage - 1) / 5) * 100)}% complete`}
                  </p>
                </div>
              </div>

              {/* Current Stage Content */}
              {impactWizardStage <= 5 && (
                <>
                  {/* Stage Header */}
                  {(() => {
                    const stage = IMPACT_WIZARD_STAGES[impactWizardStage - 1];
                    const Icon = stage.icon;
                    const categoryData = impactDefinitions?.[stage.key] || {};
                    return (
                      <div className={`${stage.bgColor} border rounded-lg p-4`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl ${stage.bgColor} flex items-center justify-center`}>
                            <Icon className={`w-6 h-6 ${stage.color}`} />
                          </div>
                          <div className="flex-1">
                            <h3 className={`font-semibold text-lg ${stage.color}`}>{stage.label}</h3>
                            <p className="text-sm text-slate-600">
                              Define what High, Medium, Low, and No Impact means for {stage.label.toLowerCase().replace(" impact", "")} scenarios.
                            </p>
                          </div>
                          <Badge variant="outline" className="bg-white">
                            {Object.keys(categoryData).length} / 4 defined
                          </Badge>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Impact Level Cards */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {IMPACT_DEFINITION_LEVELS.map((level) => {
                      const stage = IMPACT_WIZARD_STAGES[impactWizardStage - 1];
                      const levelData = impactDefinitions?.[stage.key]?.[level.key] || {};
                      return (
                        <Card key={level.key} className={`border-2 ${level.color.includes("red") ? "border-red-200" : level.color.includes("amber") ? "border-amber-200" : level.color.includes("emerald") ? "border-emerald-200" : "border-slate-200"}`}>
                          <CardHeader className="pb-3">
                            <div className="flex items-center gap-2">
                              <Badge className={level.color}>{level.label}</Badge>
                              {levelData.title && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div>
                              <Label className="text-xs font-medium">Title</Label>
                              <Input
                                value={levelData.title || ""}
                                onChange={(e) => updateImpactDefinition(stage.key, level.key, "title", e.target.value)}
                                placeholder={`e.g., ${level.label} ${stage.label}`}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-xs font-medium">Description</Label>
                              <Textarea
                                value={levelData.description || ""}
                                onChange={(e) => updateImpactDefinition(stage.key, level.key, "description", e.target.value)}
                                placeholder="Describe what this impact level means..."
                                className="mt-1"
                                rows={2}
                              />
                            </div>
                            <div>
                              <Label className="text-xs font-medium">Examples (one per line)</Label>
                              <Textarea
                                value={(levelData.examples || []).join("\n")}
                                onChange={(e) => updateImpactDefinitionExamples(stage.key, level.key, e.target.value)}
                                placeholder="• Example 1&#10;• Example 2"
                                className="mt-1 text-xs"
                                rows={2}
                              />
                            </div>
                            {/* Financial-specific thresholds */}
                            {stage.key === "financial" && (
                              <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                                <div>
                                  <Label className="text-xs">Revenue Loss %</Label>
                                  <Input
                                    type="number"
                                    value={levelData.thresholds?.revenue_loss_percentage || ""}
                                    onChange={(e) => updateImpactDefinition(stage.key, level.key, "thresholds", {
                                      ...levelData.thresholds,
                                      revenue_loss_percentage: parseFloat(e.target.value) || 0
                                    })}
                                    placeholder="e.g., 10"
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Min Cost ($)</Label>
                                  <Input
                                    type="number"
                                    value={levelData.thresholds?.recovery_cost_min || ""}
                                    onChange={(e) => updateImpactDefinition(stage.key, level.key, "thresholds", {
                                      ...levelData.thresholds,
                                      recovery_cost_min: parseFloat(e.target.value) || 0
                                    })}
                                    placeholder="e.g., 1000000"
                                    className="mt-1"
                                  />
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Stage 6: Preview & Save */}
              {impactWizardStage === 6 && (
                <>
                  <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <Eye className="w-6 h-6 text-sky-700" />
                      <div>
                        <h3 className="font-semibold text-indigo-800">Preview & Save Impact Definitions</h3>
                        <p className="text-sm text-sky-700">
                          Review all your definitions below. When you save, all Business Users will be notified that BIA Configuration is ready.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Completion Summary */}
                  <div className="grid grid-cols-5 gap-2">
                    {IMPACT_WIZARD_STAGES.slice(0, 5).map((stage) => {
                      const Icon = stage.icon;
                      const categoryData = impactDefinitions?.[stage.key] || {};
                      const definedCount = Object.keys(categoryData).filter(k => categoryData[k]?.title).length;
                      const isComplete = definedCount >= 2; // At least HIGH and one other
                      return (
                        <div key={stage.id} className={`p-3 rounded-lg border text-center ${isComplete ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
                          <Icon className={`w-5 h-5 mx-auto ${isComplete ? "text-emerald-600" : "text-amber-600"}`} />
                          <p className="text-xs font-medium mt-1">{stage.label.split(" ")[0]}</p>
                          <p className={`text-[10px] ${isComplete ? "text-emerald-600" : "text-amber-600"}`}>
                            {definedCount}/4 levels
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  {/* Full Preview Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Impact Definitions Matrix</CardTitle>
                      <CardDescription>This is how the definitions will appear to Business Users</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-100">
                              <TableHead className="font-semibold">Category</TableHead>
                              <TableHead className="font-semibold text-red-700">High</TableHead>
                              <TableHead className="font-semibold text-amber-700">Medium</TableHead>
                              <TableHead className="font-semibold text-emerald-700">Low</TableHead>
                              <TableHead className="font-semibold text-slate-500">No Impact</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {IMPACT_WIZARD_STAGES.slice(0, 5).map((stage) => {
                              const catData = impactDefinitions?.[stage.key] || {};
                              const Icon = stage.icon;
                              return (
                                <TableRow key={stage.key}>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Icon className={`w-4 h-4 ${stage.color}`} />
                                      <span className="font-medium">{stage.label}</span>
                                    </div>
                                  </TableCell>
                                  {["high", "medium", "low", "none"].map((level) => (
                                    <TableCell key={level} className="text-sm">
                                      <div className="font-medium">{catData[level]?.title || <span className="text-slate-400 italic">Not defined</span>}</div>
                                      <div className="text-xs text-muted-foreground line-clamp-2">
                                        {catData[level]?.description || ""}
                                      </div>
                                    </TableCell>
                                  ))}
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Version Info */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="bg-white">Current Version: {impactDefinitionsVersion}</Badge>
                      {updateStatus?.summary?.total_pending > 0 && (
                        <Badge variant="destructive" className="flex items-center gap-1">
                          <Bell className="w-3 h-3" />
                          {updateStatus.summary.total_pending} BIAs pending update
                        </Badge>
                      )}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setImpactWizardStage(1)}>
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit Definitions
                    </Button>
                  </div>
                </>
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setImpactWizardStage(Math.max(1, impactWizardStage - 1))}
                  disabled={impactWizardStage === 1}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                
                <div className="flex items-center gap-2">
                  {impactWizardStage < 6 && (
                    <Button
                      variant="outline"
                      onClick={() => setImpactWizardStage(6)}
                    >
                      Skip to Preview
                    </Button>
                  )}
                  
                  {impactWizardStage < 6 ? (
                    <Button
                      onClick={() => setImpactWizardStage(impactWizardStage + 1)}
                    >
                      Next: {IMPACT_WIZARD_STAGES[impactWizardStage]?.label.split(" ")[0]}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      onClick={saveImpactDefinitions}
                      disabled={saving}
                      className="bg-gradient-to-r from-sky-700 to-teal-600 hover:from-indigo-700 hover:to-purple-700"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save Impact Definitions & Notify Users
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Authority Matrix - Dropdown from Executives */}
          {currentStep === 2 && (
            <div className="space-y-6" data-testid="step-authority-matrix">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Purpose:</strong> Map executives from your organization to BCM roles. 
                  Select from executives defined in <span className="font-semibold">Organization → Key Executives & Decision Makers</span>.
                </p>
              </div>
              
              {executivesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-sky-700" />
                  <span className="ml-2 text-slate-600">Loading executives...</span>
                </div>
              ) : executives.length === 0 ? (
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="pt-6">
                    <div className="text-center py-6">
                      <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
                      <h3 className="font-semibold text-lg text-slate-900 mb-2">No Executives Defined</h3>
                      <p className="text-slate-600 mb-4">
                        Please add executives in <span className="font-semibold">Organization → Organization Management → Key Executives & Decision Makers</span> first.
                      </p>
                      <Button variant="outline" onClick={() => window.location.href = "/org-management"}>
                        <Users className="w-4 h-4 mr-2" />
                        Go to Organization Management
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* BCM Role Mapping */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Shield className="w-5 h-5 text-sky-700" />
                        BCM Authority Role Assignments
                      </CardTitle>
                      <CardDescription>
                        Select an executive for each BCM role. Contact information will be auto-populated.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {BCM_AUTHORITY_ROLES.map((bcmRole) => {
                          const assignedRole = authorityMatrix.roles?.find(r => r.role_type === bcmRole.role_type);
                          return (
                            <div key={bcmRole.role_type} className="grid md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg border">
                              <div>
                                <Label className="text-sm font-semibold text-slate-700">{bcmRole.role_name}</Label>
                                <p className="text-xs text-slate-500 mt-1">{bcmRole.description}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-slate-500">Assigned Executive</Label>
                                <Select 
                                  value={assignedRole?.executive_id || "__unassigned__"} 
                                  onValueChange={(execId) => {
                                    if (execId === "__unassigned__") {
                                      // Clear assignment
                                      setAuthorityMatrix(prev => ({
                                        ...prev,
                                        roles: prev.roles?.filter(r => r.role_type !== bcmRole.role_type) || []
                                      }));
                                      return;
                                    }
                                    const exec = executives.find(e => e.id === execId);
                                    if (exec) {
                                      setAuthorityMatrix(prev => {
                                        const existingRoles = prev.roles?.filter(r => r.role_type !== bcmRole.role_type) || [];
                                        return {
                                          ...prev,
                                          roles: [
                                            ...existingRoles,
                                            {
                                              id: `${bcmRole.role_type}_${Date.now()}`,
                                              role_type: bcmRole.role_type,
                                              role_name: bcmRole.role_name,
                                              executive_id: exec.id,
                                              person_name: exec.name,
                                              title: exec.designation,
                                              email: exec.email,
                                              phone: exec.phone || "",
                                              status: "active"
                                            }
                                          ]
                                        };
                                      });
                                    }
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select executive..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__unassigned__">-- Not Assigned --</SelectItem>
                                    {executives.map((exec) => (
                                      <SelectItem key={exec.id} value={exec.id}>
                                        {exec.name} - {exec.designation}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                {assignedRole ? (
                                  <div className="text-sm">
                                    <p className="text-slate-700 font-medium">{assignedRole.person_name}</p>
                                    <p className="text-slate-500 text-xs">{assignedRole.title}</p>
                                    <p className="text-slate-500 text-xs">{assignedRole.email}</p>
                                    {assignedRole.phone && <p className="text-slate-500 text-xs">{assignedRole.phone}</p>}
                                  </div>
                                ) : (
                                  <div className="flex items-center text-amber-600 text-sm">
                                    <AlertTriangle className="w-4 h-4 mr-1" />
                                    Not assigned
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Summary */}
                  <div className="flex items-center justify-between p-4 bg-slate-100 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className={`w-5 h-5 ${authorityMatrix.roles?.length > 0 ? "text-green-500" : "text-slate-400"}`} />
                      <span className="text-sm text-slate-700">
                        {authorityMatrix.roles?.length || 0} of {BCM_AUTHORITY_ROLES.length} roles assigned
                      </span>
                    </div>
                    <Badge variant={authorityMatrix.roles?.length >= 3 ? "default" : "secondary"}>
                      {authorityMatrix.roles?.length >= 3 ? "Ready" : "Needs Assignment"}
                    </Badge>
                  </div>
                </>
              )}
              
              {/* Save Button */}
              <div className="flex justify-end pt-4">
                <Button 
                  onClick={saveAuthorityMatrix} 
                  disabled={saving || authorityMatrix.roles?.length === 0}
                  data-testid="save-authority-matrix-btn"
                >
                  {saving ? "Saving..." : "Save Authority Matrix"}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Vendor Criticality (Inherited from BIA) */}
          {currentStep === 3 && (
            <div className="space-y-6" data-testid="step-vendor-criticality">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Vendor Criticality Inheritance:</strong> Vendors inherit their criticality requirements from 
                  Operational BIA submissions. The most aggressive RTO and highest criticality tier from all dependent 
                  processes determines the vendor's requirements.
                </p>
              </div>
              
              {vendorCriticalityLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-sky-700" />
                  <span className="ml-2 text-slate-600">Loading vendor criticality data...</span>
                </div>
              ) : (
                <>
                  {/* Summary Stats */}
                  <div className="grid md:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-purple-50 to-white">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                          <Truck className="w-8 h-8 text-purple-600" />
                          <div>
                            <p className="text-2xl font-bold text-slate-900">{vendorCriticality.summary?.total_vendors || 0}</p>
                            <p className="text-xs text-slate-500">Total Vendors</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-emerald-50 to-white">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                          <Link2 className="w-8 h-8 text-emerald-600" />
                          <div>
                            <p className="text-2xl font-bold text-slate-900">{vendorCriticality.summary?.vendors_with_dependencies || 0}</p>
                            <p className="text-xs text-slate-500">With Process Dependencies</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-red-50 to-white">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                          <Target className="w-8 h-8 text-red-600" />
                          <div>
                            <p className="text-2xl font-bold text-slate-900">{vendorCriticality.summary?.tier_1_critical_vendors || 0}</p>
                            <p className="text-xs text-slate-500">Tier 1 Critical</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-amber-50 to-white">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                          <Clock className="w-8 h-8 text-amber-600" />
                          <div>
                            <p className="text-2xl font-bold text-slate-900">{vendorCriticality.summary?.avg_rto_hours || 0}h</p>
                            <p className="text-xs text-slate-500">Avg Required RTO</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Inheritance Logic Explanation */}
                  <Card className="bg-slate-50 border-slate-200">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-slate-500 mt-0.5" />
                        <div className="text-sm text-slate-600">
                          <p className="font-semibold text-slate-700 mb-2">How Vendor Criticality Inheritance Works:</p>
                          <ul className="space-y-1">
                            <li><strong>Criticality Tier:</strong> {vendorCriticality.inheritance_logic?.criticality_tier || "MAX (most critical tier from dependent processes)"}</li>
                            <li><strong>RTO (Recovery Time):</strong> {vendorCriticality.inheritance_logic?.rto_hours || "MIN (most aggressive RTO from dependent processes)"}</li>
                            <li><strong>RPO (Recovery Point):</strong> {vendorCriticality.inheritance_logic?.rpo_hours || "MIN (most aggressive RPO from dependent processes)"}</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Vendors Table */}
                  {vendorCriticality.vendors?.length === 0 ? (
                    <Card className="border-amber-200 bg-amber-50">
                      <CardContent className="pt-6">
                        <div className="text-center py-6">
                          <Truck className="w-12 h-12 mx-auto mb-4 text-amber-500" />
                          <h3 className="font-semibold text-lg text-slate-900 mb-2">No Vendor Data Yet</h3>
                          <p className="text-slate-600 mb-4">
                            Vendor criticality is derived from Operational BIA submissions. Once business processes 
                            add vendor dependencies in their BIAs, the inherited criticality will appear here.
                          </p>
                          <Button variant="outline" onClick={() => window.location.href = "/operational-bia"}>
                            <FileCheck className="w-4 h-4 mr-2" />
                            Go to Operational BIA
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Truck className="w-5 h-5 text-purple-600" />
                          Vendor Criticality Summary
                        </CardTitle>
                        <CardDescription>
                          Requirements inherited from dependent business processes
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Vendor</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Processes</TableHead>
                              <TableHead>Criticality</TableHead>
                              <TableHead>Required RTO</TableHead>
                              <TableHead>Required RPO</TableHead>
                              <TableHead>Driving Process</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {vendorCriticality.vendors?.map((vendor) => (
                              <TableRow key={vendor.vendor_id}>
                                <TableCell>
                                  <div>
                                    <p className="font-medium text-slate-900">{vendor.vendor_name}</p>
                                    {vendor.contact_email && (
                                      <p className="text-xs text-slate-500">{vendor.contact_email}</p>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="text-xs capitalize">
                                    {vendor.vendor_type?.replace("_", " ") || "N/A"}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="secondary">{vendor.process_count || 0}</Badge>
                                </TableCell>
                                <TableCell>
                                  {vendor.inherited_criticality_tier ? (
                                    <Badge className={
                                      vendor.inherited_criticality_tier === "tier_1" ? "bg-red-100 text-red-700" :
                                      vendor.inherited_criticality_tier === "tier_2" ? "bg-amber-100 text-amber-700" :
                                      vendor.inherited_criticality_tier === "tier_3" ? "bg-blue-100 text-blue-700" :
                                      "bg-slate-100 text-slate-600"
                                    }>
                                      {formatTier(vendor.inherited_criticality_tier).toUpperCase()}
                                    </Badge>
                                  ) : (
                                    <span className="text-slate-400 text-sm">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {vendor.inherited_rto_hours !== null ? (
                                    <span className="font-semibold text-slate-900">{vendor.inherited_rto_hours}h</span>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {vendor.inherited_rpo_hours !== null ? (
                                    <span className="font-semibold text-slate-900">{vendor.inherited_rpo_hours}h</span>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {vendor.driving_process ? (
                                    <span className="text-sm text-purple-600">{vendor.driving_process}</span>
                                  ) : (
                                    <span className="text-slate-400 text-sm">-</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}

                  {/* Refresh Button */}
                  <div className="flex justify-end">
                    <Button variant="outline" onClick={fetchVendorCriticality} disabled={vendorCriticalityLoading}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${vendorCriticalityLoading ? "animate-spin" : ""}`} />
                      Refresh Data
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 4: Facility Criticality (Inherited from BIA) */}
          {currentStep === 4 && (
            <div className="space-y-6" data-testid="step-facility-criticality">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Facility Criticality Inheritance:</strong> Facilities inherit their criticality requirements from 
                  Operational BIA submissions. The most aggressive RTO and highest criticality tier from all dependent 
                  processes determines the facility's requirements.
                </p>
              </div>
              
              {facilityCriticalityLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-sky-700" />
                  <span className="ml-2 text-slate-600">Loading facility criticality data...</span>
                </div>
              ) : (
                <>
                  {/* Summary Stats */}
                  <div className="grid md:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-indigo-50 to-white">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                          <Building2 className="w-8 h-8 text-sky-700" />
                          <div>
                            <p className="text-2xl font-bold text-slate-900">{facilityCriticality.summary?.total_facilities || 0}</p>
                            <p className="text-xs text-slate-500">Total Facilities</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-emerald-50 to-white">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                          <Link2 className="w-8 h-8 text-emerald-600" />
                          <div>
                            <p className="text-2xl font-bold text-slate-900">{facilityCriticality.summary?.facilities_with_dependencies || 0}</p>
                            <p className="text-xs text-slate-500">With Process Dependencies</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-red-50 to-white">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                          <Target className="w-8 h-8 text-red-600" />
                          <div>
                            <p className="text-2xl font-bold text-slate-900">{facilityCriticality.summary?.tier_1_critical_facilities || 0}</p>
                            <p className="text-xs text-slate-500">Tier 1 Critical</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-amber-50 to-white">
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-3">
                          <Clock className="w-8 h-8 text-amber-600" />
                          <div>
                            <p className="text-2xl font-bold text-slate-900">{facilityCriticality.summary?.avg_rto_hours || 0}h</p>
                            <p className="text-xs text-slate-500">Avg Required RTO</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Inheritance Logic Explanation */}
                  <Card className="bg-slate-50 border-slate-200">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-slate-500 mt-0.5" />
                        <div className="text-sm text-slate-600">
                          <p className="font-semibold text-slate-700 mb-2">How Inheritance Works:</p>
                          <ul className="space-y-1">
                            <li><strong>Criticality Tier:</strong> {facilityCriticality.inheritance_logic?.criticality_tier || "MAX (most critical tier from dependent processes)"}</li>
                            <li><strong>RTO (Recovery Time):</strong> {facilityCriticality.inheritance_logic?.rto_hours || "MIN (most aggressive RTO from dependent processes)"}</li>
                            <li><strong>RPO (Recovery Point):</strong> {facilityCriticality.inheritance_logic?.rpo_hours || "MIN (most aggressive RPO from dependent processes)"}</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Facilities Table */}
                  {facilityCriticality.facilities?.length === 0 ? (
                    <Card className="border-amber-200 bg-amber-50">
                      <CardContent className="pt-6">
                        <div className="text-center py-6">
                          <Building2 className="w-12 h-12 mx-auto mb-4 text-amber-500" />
                          <h3 className="font-semibold text-lg text-slate-900 mb-2">No Facility Data Yet</h3>
                          <p className="text-slate-600 mb-4">
                            Facility criticality is derived from Operational BIA submissions. Once business processes 
                            are mapped to facilities in their BIAs, the inherited criticality will appear here.
                          </p>
                          <div className="flex gap-3 justify-center">
                            <Button variant="outline" onClick={() => window.location.href = "/org-location-setup"}>
                              <Building2 className="w-4 h-4 mr-2" />
                              Add Locations
                            </Button>
                            <Button variant="outline" onClick={() => window.location.href = "/operational-bia"}>
                              <FileCheck className="w-4 h-4 mr-2" />
                              Go to Operational BIA
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-sky-700" />
                          Facility Criticality Summary
                        </CardTitle>
                        <CardDescription>
                          Requirements inherited from dependent business processes
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Facility</TableHead>
                              <TableHead>Source</TableHead>
                              <TableHead>Processes</TableHead>
                              <TableHead>Criticality</TableHead>
                              <TableHead>Required RTO</TableHead>
                              <TableHead>Required RPO</TableHead>
                              <TableHead>Driving Process</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {facilityCriticality.facilities?.map((facility) => (
                              <TableRow key={facility.facility_id}>
                                <TableCell>
                                  <div>
                                    <p className="font-medium text-slate-900">{facility.facility_name}</p>
                                    <p className="text-xs text-slate-500">{facility.city || facility.address || "No address"}</p>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="text-xs">
                                    {facility.source === "org_location_setup" ? "Org Setup" : "BIA"}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="secondary">{facility.process_count || 0}</Badge>
                                </TableCell>
                                <TableCell>
                                  {facility.inherited_criticality_tier ? (
                                    <Badge className={
                                      facility.inherited_criticality_tier === "tier_1" ? "bg-red-100 text-red-700" :
                                      facility.inherited_criticality_tier === "tier_2" ? "bg-amber-100 text-amber-700" :
                                      facility.inherited_criticality_tier === "tier_3" ? "bg-blue-100 text-blue-700" :
                                      "bg-slate-100 text-slate-600"
                                    }>
                                      {formatTier(facility.inherited_criticality_tier).toUpperCase()}
                                    </Badge>
                                  ) : (
                                    <span className="text-slate-400 text-sm">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {facility.inherited_rto_hours !== null ? (
                                    <span className="font-semibold text-slate-900">{facility.inherited_rto_hours}h</span>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {facility.inherited_rpo_hours !== null ? (
                                    <span className="font-semibold text-slate-900">{facility.inherited_rpo_hours}h</span>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {facility.driving_process ? (
                                    <span className="text-sm text-sky-700">{facility.driving_process}</span>
                                  ) : (
                                    <span className="text-slate-400 text-sm">-</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}

                  {/* Refresh Button */}
                  <div className="flex justify-end">
                    <Button variant="outline" onClick={fetchFacilityCriticality} disabled={facilityCriticalityLoading}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${facilityCriticalityLoading ? "animate-spin" : ""}`} />
                      Refresh Data
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 5: Review & Activate */}
          {currentStep === 5 && (
            <div className="space-y-6" data-testid="step-review">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm text-emerald-800">
                  <strong>Review your BIA Configuration:</strong> Once all steps are complete, 
                  Business Users will be able to create BIA assessments using these configurations.
                </p>
              </div>
              
              {/* Configuration Summary */}
              <div className="grid md:grid-cols-2 gap-4">
                <Card className={configStatus.impact_matrix_configured ? "border-emerald-200" : "border-amber-200"}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      {configStatus.impact_matrix_configured ? (
                        <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                      ) : (
                        <AlertTriangle className="w-8 h-8 text-amber-500" />
                      )}
                      <div>
                        <p className="font-semibold">Impact Over Time Matrix</p>
                        <p className="text-sm text-slate-500">
                          {configStatus.impact_matrix_configured ? "Configured" : "Not configured"}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className={configStatus.authority_matrix_defined ? "border-emerald-200" : "border-amber-200"}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      {configStatus.authority_matrix_defined ? (
                        <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                      ) : (
                        <AlertTriangle className="w-8 h-8 text-amber-500" />
                      )}
                      <div>
                        <p className="font-semibold">Authority Matrix</p>
                        <p className="text-sm text-slate-500">
                          {authorityMatrix.roles?.length || 0} roles defined
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className={vendorCriticality.summary?.vendors_with_dependencies > 0 ? "border-emerald-200" : "border-slate-200"}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      {vendorCriticality.summary?.vendors_with_dependencies > 0 ? (
                        <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                      ) : (
                        <Info className="w-8 h-8 text-slate-400" />
                      )}
                      <div>
                        <p className="font-semibold">Vendor Criticality</p>
                        <p className="text-sm text-slate-500">
                          {vendorCriticality.summary?.vendors_with_dependencies || 0} vendors with inherited requirements
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className={facilityCriticality.summary?.facilities_with_dependencies > 0 ? "border-emerald-200" : "border-slate-200"}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      {facilityCriticality.summary?.facilities_with_dependencies > 0 ? (
                        <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                      ) : (
                        <Info className="w-8 h-8 text-slate-400" />
                      )}
                      <div>
                        <p className="font-semibold">Facility Criticality</p>
                        <p className="text-sm text-slate-500">
                          {facilityCriticality.summary?.facilities_with_dependencies || 0} facilities with inherited requirements
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-sky-200 bg-sky-50">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-sky-700 text-white flex items-center justify-center font-bold">
                        {getCompletionPercentage()}%
                      </div>
                      <div>
                        <p className="font-semibold">Overall Completion</p>
                        <p className="text-sm text-slate-500">
                          {getCompletionPercentage() >= 60 ? "Ready for Business Users" : "Minimum 60% required"}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {getCompletionPercentage() >= 60 ? (
                <div className="bg-emerald-100 border border-emerald-300 rounded-lg p-4 text-center">
                  <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                  <p className="font-semibold text-emerald-800 text-lg">BIA Configuration Complete!</p>
                  <p className="text-emerald-700 mt-1">
                    Business Users can now create Business Impact Assessments using these configurations.
                  </p>
                </div>
              ) : (
                <div className="bg-amber-100 border border-amber-300 rounded-lg p-4 text-center">
                  <AlertTriangle className="w-12 h-12 text-amber-600 mx-auto mb-2" />
                  <p className="font-semibold text-amber-800 text-lg">Configuration Incomplete</p>
                  <p className="text-amber-700 mt-1">
                    Please complete at least the Impact Matrix and Authority Matrix (Steps 1-2) 
                    before Business Users can create BIA assessments.
                  </p>
                </div>
              )}
            </div>
          )}
      </div>
    </WizardShell>
  );
}
